.visuallyHidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/* --- alert --------------------------------------------------------------- *
 *
 * A tinted fill and a leading rule in the tone's colour, rather than a full
 * border on all four sides. The rule is the signal; a box would be a card.
 */

.alert {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-4) var(--space-5);
  border: var(--border-width-thin) solid color-mix(in oklab, var(--tone-border) 35%, transparent);
  border-inline-start: 4px solid var(--tone-border);
  border-radius: var(--radius-md);
  background: var(--tone-bg);
  color: var(--color-text-primary);
}

.neutral {
  --tone-bg: var(--color-surface-sunken);
  --tone-border: var(--color-border-strong);
  --tone-fg: var(--color-text-secondary);
}

.info {
  --tone-bg: var(--color-info-bg);
  --tone-border: var(--color-info-emphasis);
  --tone-fg: var(--color-info-fg);
}

.success {
  --tone-bg: var(--color-success-bg);
  --tone-border: var(--color-success-emphasis);
  --tone-fg: var(--color-success-fg);
}

.warning {
  --tone-bg: var(--color-warning-bg);
  --tone-border: var(--color-warning-emphasis);
  --tone-fg: var(--color-warning-fg);
}

.danger {
  --tone-bg: var(--color-danger-bg);
  --tone-border: var(--color-danger-emphasis);
  --tone-fg: var(--color-danger-fg);
}

.alertGlyph {
  flex: none;
  margin-top: 2px;
  color: var(--tone-fg);
}

.alertBody {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  flex: 1 1 auto;
  min-width: 0;
}

.alertTitle {
  font-size: var(--text-base);
  line-height: var(--text-base-line);
  font-weight: var(--font-weight-semibold);
}

.alertText {
  font-size: var(--text-base);
  line-height: var(--text-base-line);
  color: var(--color-text-secondary);
}

.alertActions {
  display: flex;
  flex: none;
  align-items: center;
  gap: var(--space-2);
}

/* An error carries its own trace line, so it needs a little more room beneath
 * the message than an ordinary alert. */
.errorState {
  align-items: flex-start;
}

/* When a caller supplied its own context for the heading, the contract's own
 * title moves into the body so that both are said. */
.failureTitle {
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.traceId {
  margin-top: var(--space-2);
  font-size: var(--text-sm);
  color: var(--color-text-tertiary);
}

.traceId code {
  /* Selectable and obviously copyable. This is the string somebody will paste
   * into a support ticket. */
  padding: 0 var(--space-1);
  background: var(--color-surface-sunken);
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
  user-select: all;
}

/* --- empty state ----------------------------------------------------------- */

.empty {
  display: flex;
  gap: var(--space-3);
}

/* The empty state *is* the page: centred, and allowed to breathe. */
.emptyPage {
  flex-direction: column;
  align-items: center;
  padding: var(--space-8) var(--space-5);
  text-align: center;
}

/* An empty section inside a page that has other content. Left-aligned on the
 * same grid as the rows it replaces, and about the height of two of them — so
 * a band with nothing in it reads as a band with nothing in it, rather than as
 * a hole in the page. */
.emptyInline {
  flex-direction: row;
  align-items: flex-start;
  padding: var(--space-5) var(--space-3);
  text-align: start;
}

.emptyFill {
  flex: 1 1 auto;
  justify-content: center;
  min-height: 240px;
}

.emptyIcon {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  color: var(--color-text-tertiary);
}

.emptyPage .emptyIcon {
  width: 56px;
  height: 56px;
  margin-bottom: var(--space-3);
  background: var(--color-accent-bg);
  border-radius: var(--radius-full);
  color: var(--color-accent-fg);
}

.emptyInline .emptyIcon {
  width: 16px;
  height: 16px;
  /* Optically aligned with the first line of the title rather than the top of
   * the text box. */
  margin-top: 2px;
}

.emptyText {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.emptyPage .emptyText {
  align-items: center;
}

.emptyTitle {
  font-size: var(--text-base);
  line-height: var(--text-base-line);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.emptyDescription {
  max-width: 68ch;
  font-size: var(--text-base);
  line-height: var(--text-base-line);
  color: var(--color-text-secondary);
  text-wrap: pretty;
}

.emptyPage .emptyTitle {
  font-size: var(--text-lg);
  line-height: var(--text-lg-line);
}

.emptyPage .emptyDescription {
  max-width: 52ch;
}

.emptyAction {
  flex: none;
}

.emptyPage .emptyAction {
  margin-top: var(--space-2);
}

/* --- skeleton ---------------------------------------------------------------- *
 *
 * A static tint, not a shimmer. §1 anti-principles: no auto-playing motion. A
 * sweeping gradient on eight rows is motion the user did not ask for, repeating
 * for as long as the request takes, and it is exactly the kind of thing that is
 * unbearable on the tenth hour of use.
 */

.skeleton {
  display: block;
  min-width: 16px;
  height: 12px;
  background: var(--color-surface-sunken);
  border: var(--border-width-thin) solid var(--color-border-default);
  border-radius: var(--radius-sm);
}

.skeletonRows {
  display: flex;
  flex-direction: column;
}

.skeletonRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-5);
  min-height: var(--row-height);
  padding: var(--cell-padding-y) var(--space-3);
  border-bottom: var(--border-width-thin) solid var(--color-border-default);
}
